/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect } from 'react';
import styles from './AlarmSettingsModal.module.css';
import { FloModal, FloModalBodyProps, FloButtonTabs, FloCheckbox } from 'components';
import { useTranslation } from 'react-i18next';
import { Row, Col, OverlayTrigger, Tooltip } from 'react-bootstrap';
import ContentLoader from "react-content-loader";
import Message from 'containers/common/Message';
import { AlarmSeverity, severityToString, stringToSeverity, AlarmSettingStatus, DeliveryMediumEnabled, User, Device } from 'model';
import { AlarmSettingsPayload } from 'actions';
import classnames from 'classnames';

type CheckboxProps = {
  mediumType: 'email' | 'call' | 'sms',
  settingsRow: AlarmSettingStatus,
  updateState: (alarmId: number, alarmSettingsPayload: AlarmSettingsPayload) => void,
}

const Checkbox: React.FC<CheckboxProps> = React.memo(({mediumType, settingsRow, updateState}) => {
  const { t } = useTranslation();
  const column =
    <div className={styles.checkboxWrapper}>
      { settingsRow.alarm.deliveryMediums[mediumType].supported && settingsRow.alarm.deliveryMediums[mediumType].default.home.supported &&
        <FloCheckbox
          className   = {styles.checkbox}
          checked     = {settingsRow[mediumType] === DeliveryMediumEnabled.TRUE}
          mixed       = {settingsRow[mediumType] === DeliveryMediumEnabled.MIXED}
          onChange    = {() => updateState(settingsRow.alarm.id, { [`${mediumType}Enabled`]: settingsRow[mediumType] !== DeliveryMediumEnabled.TRUE })}
        />
      }
    </div>;

  return (
    <Col className={styles.center}>
      { settingsRow[mediumType] === DeliveryMediumEnabled.MIXED ?
        <OverlayTrigger
          key={`tooltip-${settingsRow.alarm.id}-${mediumType}`}
          placement={'auto'}
          overlay={
            <Tooltip id={`tooltip-${settingsRow.alarm.id}-${mediumType}`}>
              <span className={styles.tooltip}>{t('alert-settings-mixed-value-tooltip')}</span>
            </Tooltip>
          }
        >
          {column}
        </OverlayTrigger>
        :
        column
      }
    </Col>
  );
})

type AlarmSettingsListRowProps = {
  settingsRow: AlarmSettingStatus,
  updateState: (alarmId: number, alarmSettingsPayload: AlarmSettingsPayload) => void;
}

const AlarmSettingsListRow: React.FC<AlarmSettingsListRowProps> = React.memo(({settingsRow, updateState}) => {
  return (
    <>
      <Row className={styles.settingsRow}>
        <Col className={styles.firstColumn}>
          <span className={classnames({ [styles.nested]: settingsRow.isNested })}>{settingsRow.alarm.displayName}</span>
        </Col>
        <Checkbox mediumType={'email'} settingsRow={settingsRow} updateState={updateState} />
        <Checkbox mediumType={'sms'} settingsRow={settingsRow} updateState={updateState} />
        <Checkbox mediumType={'call'} settingsRow={settingsRow} updateState={updateState} />
      </Row>
      <Row><Col xs="12"><div className={styles.separator}></div></Col></Row>
    </>
  )
})

type AlarmSettingsListProps = {
  settings: AlarmSettingStatus[];
  updateState: (alarmId: number, alarmSettingsPayload: AlarmSettingsPayload) => void;
}

const AlarmSettingsList: React.FC<AlarmSettingsListProps> = React.memo(({settings, updateState}) => {
  const { t } = useTranslation();
  return (
    <Row className={styles.listContainer}>
      <Col xs="12">
        <Row className={styles.header}>
          <Col className={styles.firstColumn}>{t('Event')}</Col>
          <Col className={styles.center}>{t('Email')}</Col>
          <Col className={styles.center}>{t('Text')}</Col>
          <Col className={styles.center}>{t('Phone')}</Col>
        </Row>
        <Row><Col xs="12"><div className={styles.separator}></div></Col></Row>
        <Row className={styles.rowWrapper}>
          <Col xs="12">
            { settings.map((s, index) => <AlarmSettingsListRow key={`alarm-settings-list-row-${index}`} settingsRow={s} updateState={updateState} />) }
          </Col>
        </Row>
      </Col>
    </Row>
  )
})

interface AlarmSettingsModalBodyProps extends FloModalBodyProps {
  settings:         AlarmSettingStatus[];
  currentTab:       AlarmSeverity;
  updateCurrentTab: (currentTab: AlarmSeverity) => void;
  updateState:      (alarmId: number, alarmSettingsPayload: AlarmSettingsPayload) => void;
  onSubmit:         (userId?: string) => void;
  user?:            User;
  device?:          Device;
  locationIds?:     string[];
}

const AlarmSettingsModalBody: React.FC<AlarmSettingsModalBodyProps> = React.memo(({
  bindSubmitForm,
  settings,
  currentTab,
  updateCurrentTab,
  updateState,
  onSubmit,
  user,
  device,
  locationIds,
}) => {
  const { t } = useTranslation();

  bindSubmitForm(async () => {
    onSubmit(user?.id);
  });

  const getInfoText = () => {
    if (device) {
      return t('alarm-settings-info-device-title', { deviceName: device.nickname || device.macAddress })
    } else if (locationIds) {
      return user ?
        t('alarm-settings-info-user-location-title', { count: locationIds.length, userName: `${user.firstName} ${user.lastName}` })
      :
        t('alarm-settings-info-location-title', { count: locationIds.length })
    } else {
      return '';
    }
  }

  return (
    <Row className={styles.bodyContainer}>
      <Col xs="12">
        <Row>
          <Col xs="12">
            <Message />
          </Col>
        </Row>
        <Row>
          <Col xs="12">
            <div
              className={styles.subtitle}
              dangerouslySetInnerHTML={{__html: getInfoText() }}
            ></div>
          </Col>
        </Row>
        <Row>
          <Col xs="12">
            <FloButtonTabs
              className = {styles.tabsContainer}
              tabs = {[
                { id: 'critical', label: () => t('Critical Alerts') },
                { id: 'warning', label: () => t('Warnings') },
                { id: 'info', label: () => t('Informative Alerts') },
              ]}
              selected  = {severityToString(currentTab)}
              onSelect  = {currentTab => updateCurrentTab(stringToSeverity(currentTab))}
            >
              <AlarmSettingsList settings={settings} updateState={updateState} />
            </FloButtonTabs>
          </Col>
        </Row>
      </Col>
    </Row>
  );
});

const textWidth = (min: number, max: number) => Math.floor(Math.random() * max) + min;
const AlarmSettingsSkeleton: React.FC<{}> = () => (
  <ContentLoader
    height={400}
    width={'100%'}
    speed={3}
  >
    <rect x="0" y="0" rx="3" ry="3" width="400" height="20" />

    <rect x="0" y="50" rx="3" ry="3" width="110" height="30" /> 
    <rect x="120" y="50" rx="3" ry="3" width="110" height="30" /> 
    <rect x="240" y="50" rx="3" ry="3" width="110" height="30" /> 
    
    
    <rect x="0" y="110" rx="3" ry="3" width="50" height="20" />
    <rect x="45%" y="110" rx="3" ry="3" width="50" height="20" />
    <rect x="65%" y="110" rx="3" ry="3" width="50" height="20" />
    <rect x="85%" y="110" rx="3" ry="3" width="50" height="20" />

    <rect x="0" y="140" rx="3" ry="3" width={'100%'} height="1" /> 
    
    
    {
      [150, 190, 230, 270, 310].map(y => (
        <React.Fragment key={y}>
          <rect x="0" y={y + 3} rx="3" ry="3" width={textWidth(50, 190)} height="15" />
          <rect x="47%" y={y} rx="3" ry="3" width="20" height="20" />
          <rect x="67%" y={y} rx="3" ry="3" width="20" height="20" />
          <rect x="87%" y={y} rx="3" ry="3" width="20" height="20" />

          <rect x="0" y={y + 30} rx="3" ry="3" width={'100%'} height="1" /> 
        </React.Fragment>
      ))
    }


  </ContentLoader>
)

type AlarmSettingsModalProps = {
  show:                       boolean;
  onHide:                     () => void;
  device?:                    Device;
  locationIds?:               string[];
  user?:                      User;
  settings:                   AlarmSettingStatus[];
  currentTab:                 AlarmSeverity;
  updateCurrentTab:           (currentTab: AlarmSeverity) => void;
  fetchNotificationSettings:  (userId?: string, device?: Device, locationIds?: string[]) => void;
  updateNotificationState:    (alarmId: number, alarmSettingsPayload: AlarmSettingsPayload) => void;
  saveNotificationSettings:   (userId?: string) => void;
  isSubmitting:               boolean;
  hasError:                   boolean;
  hasDirtySettings:           boolean;
  isLoading:                  boolean;
}

export const AlarmSettingsModal: React.FC<AlarmSettingsModalProps> = React.memo(({
  show, settings, currentTab, updateCurrentTab, fetchNotificationSettings, saveNotificationSettings, updateNotificationState,
  device, user, locationIds, hasError, isLoading, hasDirtySettings, ...rest
}) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (show) {
      fetchNotificationSettings(user?.id, device, locationIds);
    }
  }, [show])

  return (
    <FloModal
      {...rest}
      show            = {show}
      name            = "set-alarm-settings-modal"
      title           = {t('Manage Notifications')}
      className       = {styles.root}
      disableButtons  = {isLoading || !hasDirtySettings}
      body            = { (props) => !isLoading ?
        <AlarmSettingsModalBody
          {...props}
          settings          = {settings}
          currentTab        = {currentTab}
          updateCurrentTab  = {updateCurrentTab}
          updateState       = {updateNotificationState}
          onSubmit          = {saveNotificationSettings}
          user              = {user}
          device            = {device}
          locationIds       = {locationIds}
        />
        :
        <AlarmSettingsSkeleton />
      }
    />
  )
})